import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEye, FaEdit, FaTrash, FaFilePdf } from 'react-icons/fa';
import { toast } from 'react-toastify';
import OfferService, { Offer } from '../../sales/services/OfferService';

interface ContactOffersTabProps {
  partnerId: number;
}

export default function ContactOffersTab({ partnerId }: ContactOffersTabProps): React.JSX.Element {
  const navigate = useNavigate();
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOffers();
  }, [partnerId]);

  const loadOffers = async () => {
    setLoading(true);
    try {
      const data = await OfferService.listByPartner(partnerId);
      setOffers(data);
    } catch (err) {
      console.error('Failed to load offers:', err);
      toast.error('Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate(`/offers/new?partner_id=${partnerId}`);
  };

  const handleView = (offer: Offer) => {
    navigate(`/offers/${offer.id}`);
  };

  const handleEdit = (offer: Offer, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/offers/${offer.id}/edit`);
  };

  const handleDelete = async (offer: Offer, e: React.MouseEvent) => {
    e.stopPropagation();
    const confirmed = window.confirm(`Delete offer "${offer.title}"?`);
    if (!confirmed) return;

    try {
      await OfferService.delete(offer.id);
      toast.success('Offer deleted');
      loadOffers();
    } catch (err: any) {
      console.error('Failed to delete offer:', err);
      const message = err?.response?.data?.detail || 'Delete failed';
      toast.error(message);
    }
  };

  const handleDownloadPdf = async (offer: Offer, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const blob = await OfferService.downloadPdf(offer.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Offer_${offer.offer_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF:', err);
      toast.error('Failed to download PDF');
    }
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE');
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
        Loading...
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#021a4a' }}>
          Offers ({offers.length})
        </h3>
        <button
          onClick={handleCreate}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '6px',
            background: '#021a4a',
            color: 'white',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <FaPlus size={12} />
          Create offer
        </button>
      </div>

      {/* Table */}
      {offers.length === 0 ? (
        <div
          style={{
            padding: '60px',
            textAlign: 'center',
            color: '#888',
            background: '#f8f9fc',
            borderRadius: '8px',
          }}
        >
          <p style={{ margin: 0, fontSize: '16px' }}>No offers yet</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
            Click "Create offer" to get started.
          </p>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fc', borderBottom: '2px solid #e0e3ef' }}>
              <th style={thStyle}>No.</th>
              <th style={thStyle}>Title</th>
              <th style={thStyle}>Project</th>
              <th style={{ ...thStyle, width: '100px' }}>Status</th>
              <th style={{ ...thStyle, width: '120px', textAlign: 'right' }}>Amount</th>
              <th style={{ ...thStyle, width: '120px' }}>Updated</th>
              <th style={{ ...thStyle, width: '140px', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {offers.map((offer) => {
              const statusColor = OfferService.getStatusColor(offer.status);
              return (
                <tr
                  key={offer.id}
                  style={{ borderBottom: '1px solid #e0e3ef', cursor: 'pointer' }}
                  onClick={() => handleView(offer)}
                >
                  <td style={tdStyle}>
                    <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                      {offer.offer_number}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600, color: '#021a4a' }}>{offer.title}</div>
                  </td>
                  <td style={tdStyle}>{offer.project_name || '-'}</td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        background: statusColor.bg,
                        color: statusColor.text,
                      }}
                    >
                      {OfferService.getStatusLabel(offer.status)}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                    {OfferService.formatCurrency(offer.total_gross)}
                  </td>
                  <td style={tdStyle}>{formatDate(offer.updated_at)}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleView(offer);
                        }}
                        title="Details"
                        style={actionBtnStyle}
                      >
                        <FaEye size={14} />
                      </button>
                      <button
                        onClick={(e) => handleEdit(offer, e)}
                        title="Edit"
                        style={actionBtnStyle}
                      >
                        <FaEdit size={14} />
                      </button>
                      <button
                        onClick={(e) => handleDownloadPdf(offer, e)}
                        title="PDF"
                        style={actionBtnStyle}
                      >
                        <FaFilePdf size={14} />
                      </button>
                      <button
                        onClick={(e) => handleDelete(offer, e)}
                        title="Delete"
                        style={{ ...actionBtnStyle, color: '#dc2626' }}
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: '13px',
  fontWeight: 600,
  color: '#666',
};

const tdStyle: React.CSSProperties = {
  padding: '14px 16px',
  fontSize: '14px',
  color: '#333',
};

const actionBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '6px',
  cursor: 'pointer',
  color: '#666',
  borderRadius: '4px',
};
